import { createThunkRequest, GET } from '@webapp/account/src/libs/request';
import { formatDateForFilters, getFilterParams, getReportSsid } from '@webapp/account/src/resources/lib';
import {
    ALL_TIME_PERIOD_ID,
    BASE_PERIOD_ID,
    NO_COMPARISON_TYPES,
} from '@webapp/account/src/resources/reports/comparison';
import { fetchReportsStat } from '@webapp/account/src/resources/reports/fetch';
import type { Report } from '@webapp/account/src/resources/reports/reports';
import {
    selectComparisonPeriods,
    selectReports,
    selectReportsQuestions,
} from '@webapp/account/src/resources/reports/selectors';
import type { ReportsState } from '@webapp/account/src/resources/reports/state';
import type { States } from '@webapp/account/src/resources/states';
import { selectActiveSurvey, selectMinFilterDate } from '@webapp/account/src/resources/survey/selectors';

import { today } from '@webapp/common/lib/date';
import type { Question } from '@webapp/common/resources/entities/question';

// Дубли одного и того же (период, вопрос) не запускаем: waypoint'ы могут сработать
// одновременно, а reducer.pending успевает пометить fetching только по своему arg.
const inFlight = new Set<string>();

// Догружает отчёты по дополнительным периодам сравнения. В отличие от getReports
// не синхронизирует globalFilters и не пишет filesSize/filesCount (они скалярные).
// Периоды обходим последовательно, чтобы не забивать сеть пачкой запросов.
export const getComparisonReports = createThunkRequest(
    'reports/getComparisonReports',
    async ({ reportIds }: { reportIds: Array<number> }, { getState }) => {
        const state: States = getState();
        const reportsState: ReportsState = selectReports(state);
        const { globalFilters, responsesLimit, comparison } = reportsState;
        const { id: surveyId } = selectActiveSurvey(state);
        const allReportList = selectReportsQuestions(state);
        const periods = selectComparisonPeriods(state).filter(({ id }) => id !== BASE_PERIOD_ID);
        const result: Array<{ periodId: string; reports: Array<Report> }> = [];

        for (const period of periods) {
            const reports = reportIds
                .map((id) => allReportList.find((report) => report.id === id))
                .filter((report): report is Report => Boolean(report))
                .filter((report) => {
                    const key = `${period.id}:${report.id}`;
                    const loaded = Boolean(comparison.byPeriod[period.id]?.[report.id]);

                    return (comparison.stale.has(key) || !loaded) && !inFlight.has(key);
                });

            if (!reports.length) {
                continue;
            }

            reports.forEach((report) => inFlight.add(`${period.id}:${report.id}`));

            try {
                const { reports: fetched } = await fetchReportsStat({
                    surveyId,
                    reports,
                    allReportList,
                    globalFilters,
                    responsesLimit,
                    page: 1,
                    period,
                });

                result.push({ periodId: period.id, reports: fetched });
            } finally {
                reports.forEach((report) => inFlight.delete(`${period.id}:${report.id}`));
            }
        }

        // Для типов без сравнения догружаем данные «за всё время опроса».
        const minDate = selectMinFilterDate(state);
        const allTimeReports = reportIds
            .map((id) => allReportList.find((report) => report.id === id))
            .filter((report): report is Report => Boolean(report) && NO_COMPARISON_TYPES.has(report.type))
            .filter((report) => {
                const key = `${ALL_TIME_PERIOD_ID}:${report.id}`;
                const loaded = Boolean(comparison.byPeriod[ALL_TIME_PERIOD_ID]?.[report.id]);

                return (comparison.stale.has(key) || !loaded) && !inFlight.has(key);
            });

        if (allTimeReports.length) {
            allTimeReports.forEach((report) => inFlight.add(`${ALL_TIME_PERIOD_ID}:${report.id}`));

            try {
                const { reports: fetched } = await fetchReportsStat({
                    surveyId,
                    reports: allTimeReports,
                    allReportList,
                    globalFilters,
                    responsesLimit,
                    page: 1,
                    period: { dateFrom: minDate, dateTo: today },
                });

                result.push({ periodId: ALL_TIME_PERIOD_ID, reports: fetched });
            } finally {
                allTimeReports.forEach((report) => inFlight.delete(`${ALL_TIME_PERIOD_ID}:${report.id}`));
            }
        }

        return result;
    },
    { notify: false },
);

// Облака слов по периодам сравнения: тот же get/word_stat, но с переопределёнными start/end.
export const getComparisonWordStat = createThunkRequest(
    'reports/getComparisonWordStat',
    async ({ questionIds, questions }: { questionIds: Array<number>; questions: Array<Question> }, { getState }) => {
        const state: States = getState();
        const { globalFilters } = selectReports(state);
        const { id: surveyId } = selectActiveSurvey(state);
        const periods = selectComparisonPeriods(state).filter(({ id }) => id !== BASE_PERIOD_ID);
        const result: Array<{ periodId: string; stat: Array<any> }> = [];

        for (const period of periods) {
            const filterParams = {
                ...getFilterParams(globalFilters, questions),
                start: formatDateForFilters(period.dateFrom),
                end: formatDateForFilters(period.dateTo),
            };

            const { stat } = await GET('get/word_stat', {
                questionIds: `[${questionIds.toString()}]`,
                ssid: getReportSsid(surveyId, filterParams),
                ...filterParams,
            });

            result.push({ periodId: period.id, stat });
        }

        return result;
    },
    { notify: false },
);
